import {
  DetectionEntitySummary,
  EntityInstance,
} from '@investigator/features/entities/types';
import type { SantizedDetection } from '@investigator/features/detections/pages/Detections';
import { AlertEntity, Detection } from '@investigator/shared/generated/graphql';
import { getDetectionSummary } from './getDetectionSummary';

/**
 * Converts a Detection or DetectionSummary to an EntityInstance.
 *
 * This function handles two scenarios:
 * 1. Detection object: Converts to DetectionSummary first, then maps to EntityInstance
 * 2. DetectionSummary: Directly maps to EntityInstance
 *
 * @param source - Either a Detection or DetectionSummary object
 * @param sourceEntity - Optional entity override (used when source is a Detection with multiple entities)
 * @returns EntityInstance with standardized entity data
 *
 * @example
 * // From Detection
 * const instance = getEntityInstanceFromDetection(detection, sourceEntity);
 *
 * @example
 * // From DetectionSummary
 * const instance = getEntityInstanceFromDetection(detectionSummary);
 *
 * @example
 * // With suppression flag
 * const instance = {
 *   ...getEntityInstanceFromDetection(detection, sourceEntity),
 *   entityIsSuppressed: true,
 * };
 */
export function getEntityInstanceFromDetection(
  source: Detection | SantizedDetection | DetectionEntitySummary,
  sourceEntity?: AlertEntity,
): EntityInstance {
  // Type guard: Check if it's a Detection object
  const isDetection = 'detection_id' in source && 'alert_info' in source;

  // Convert to DetectionSummary if needed
  const summary: DetectionEntitySummary = isDetection
    ? getDetectionSummary(source as Detection | SantizedDetection, sourceEntity)
    : (source as DetectionEntitySummary);

  // Map to EntityInstance
  return {
    entityId: summary.entity_id ?? undefined,
    entityName: summary.entity_name ?? undefined,
    entityType: summary.entity_type ?? undefined,
    entityCategory: summary.entity_category ?? undefined,
    tenantEntity: summary.tenant_entity ?? undefined,
    lastSeen: summary.last_seen ?? undefined,
    tenantInfo: summary.tenant_info ?? undefined,
    detection_id: summary.detection_id || undefined,
    detectionData: summary,
    agenticTriageReportId: summary.agentic_triage_report_id || undefined,
  };
}
